import { SectionHeader, NavButtons, VitalsAlert, TriageScore } from "@/components/workflow/ui";
import { SignsChecklist, VitalsGrid } from "@/components/workflow/parts";
import ClinicalScores from "@/components/ClinicalScores";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Activity, ClipboardCopy, NotebookPen } from "lucide-react";
import { toast } from "sonner";

function buildSignsSnapshot(signs, signResp) {
  const list = signs?.signs || [];
  const present = [], absent = [];
  list.forEach((s, i) => {
    const r = signResp[s.id || i] || {};
    const note = r.note ? ` (${r.note})` : "";
    if (r.status === "Present") present.push(`• ${s.sign}${note}`);
    else if (r.status === "Absent") absent.push(`• ${s.sign}${note}`);
  });
  return [
    "POSITIVE SIGNS (present):",
    present.length ? present.join("\n") : "• (none recorded)",
    "",
    "NEGATIVE SIGNS (absent):",
    absent.length ? absent.join("\n") : "• (none recorded)",
  ].join("\n");
}

export default function StepExamination({
  signs, signResp, setSignResp, findings, setFindings, vitalCfg,
  isPediatric, ageYears, patient, setScores, signsReminded, addSnapshotToNotes, onBack, onSubmit,
}) {
  const copySigns = async () => {
    try {
      await navigator.clipboard.writeText(buildSignsSnapshot(signs, signResp));
      toast.success("Signs summary copied to clipboard");
    } catch {
      toast.error("Could not copy. Your browser blocked clipboard access.");
    }
  };
  const signsToNotes = () => addSnapshotToNotes(buildSignsSnapshot(signs, signResp));

  return (
    <form onSubmit={onSubmit} data-testid="examination-section">
      <SectionHeader icon={Activity} sub="Step 4 · Doctor" title="Clinical examination" />
      {signs.intro && <p className="text-sm text-zinc-600 -mt-2 mb-4">{signs.intro}</p>}
      <div className="mb-4 flex flex-wrap gap-2">
        <Button type="button" variant="outline" size="sm" onClick={copySigns} data-testid="copy-signs-btn" className="border-zinc-300 text-zinc-700">
          <ClipboardCopy className="mr-2 h-4 w-4" /> Copy signs summary
        </Button>
        <Button type="button" variant="outline" size="sm" onClick={signsToNotes} data-testid="signs-to-notes-btn" className="border-zinc-300 text-zinc-700">
          <NotebookPen className="mr-2 h-4 w-4" /> Add signs to notes
        </Button>
      </div>
      <p className="text-sm font-semibold text-zinc-900 mb-3">Signs to look for (mark present / absent):</p>
      <SignsChecklist signs={signs} signResp={signResp} setSignResp={setSignResp} reminded={signsReminded} />
      <div className="mt-8">
        <p className="text-sm font-semibold text-zinc-900 mb-3">Vital signs</p>
        <VitalsGrid vitalCfg={vitalCfg} vitals={findings.vitals} setFindings={setFindings} />
        <VitalsAlert vitals={findings.vitals} config={vitalCfg} />
        <TriageScore vitals={findings.vitals} hide={isPediatric} />
        {isPediatric && (
          <p className="mt-3 text-xs text-zinc-500" data-testid="pediatric-vitals-note">
            Pediatric thresholds applied for age {ageYears}y. NEWS2 (adult) is hidden; a pediatric early-warning approach should be used clinically.
          </p>
        )}
      </div>
      <ClinicalScores vitals={findings.vitals} age={patient.age} pediatric={isPediatric} onScores={setScores} />
      <div className="mt-6 space-y-1.5">
        <Label>Additional examination findings</Label>
        <Textarea rows={3} data-testid="exam-notes" value={findings.examination_notes} onChange={(e) => setFindings((f) => ({ ...f, examination_notes: e.target.value }))} placeholder="Other positive / negative findings…" />
      </div>
      <NavButtons onBack={onBack} nextLabel="Reach differential diagnosis" nextTestId="submit-findings-btn" submit />
    </form>
  );
}
